import {
  CloudAngledRainIcon,
  CloudAngledRainZapIcon,
  CloudIcon,
  CloudSlowWindIcon,
  CloudSnowIcon,
  SnowIcon,
  Sun03Icon,
  SunCloud01Icon,
} from "@hugeicons/core-free-icons";
import type { IconSvgElement } from "@hugeicons/react";

export type WeatherPresentation = {
  icon: IconSvgElement;
  description: string;
  className: string;
};

const sunny: WeatherPresentation = {
  icon: Sun03Icon,
  description: "آفتابی",
  className: "[&>path]:stroke-[#F99B3D]",
};

const partlyCloudy: WeatherPresentation = {
  icon: SunCloud01Icon,
  description: "کمی ابری",
  className:
    "[&>path:nth-child(1)]:stroke-[#5FB3E7] [&>path:not(:first-child)]:stroke-[#F99B3D]",
};

const cloudy: WeatherPresentation = {
  icon: CloudIcon,
  description: "تمام ابری",
  className: "[&>path]:stroke-[#5FB3E7]",
};

const fog: WeatherPresentation = {
  icon: CloudSlowWindIcon,
  description: "مه",
  className:
    "[&>path:nth-child(1)]:stroke-[#AEAEAE] [&>path:not(:first-child)]:stroke-[#E1BE14]",
};

const drizzle: WeatherPresentation = {
  icon: CloudAngledRainIcon,
  description: "نم‌نم باران",
  className:
    "[&>path:nth-child(1)]:stroke-[#005E98] [&>path:not(:first-child)]:stroke-[#5FB3E7]",
};

const rain: WeatherPresentation = {
  icon: CloudAngledRainIcon,
  description: "باران",
  className:
    "[&>path:nth-child(1)]:stroke-[#005E98] [&>path:not(:first-child)]:stroke-[#5FB3E7]",
};

const snow: WeatherPresentation = {
  icon: CloudSnowIcon,
  description: "برف",
  className:
    "[&>path:nth-child(1)]:stroke-[#5FB3E7] [&>path:not(:first-child)]:stroke-[#005E98]",
};

const thunder: WeatherPresentation = {
  icon: CloudAngledRainZapIcon,
  description: "رعد و برق",
  className:
    "[&>path:nth-child(1)]:stroke-[#5FB3E7] [&>path:not(:first-child)]:stroke-[#E1BE14]",
};

/** WMO weather interpretation codes used by Open-Meteo. */
const weatherMap: Record<number, WeatherPresentation> = {
  0: sunny,
  1: partlyCloudy,
  2: partlyCloudy,
  3: cloudy,
  45: fog,
  48: { ...fog, description: "مه یخ‌زده" },
  51: drizzle,
  53: drizzle,
  55: drizzle,
  56: { ...drizzle, description: "نم‌نم باران یخ‌زده" },
  57: { ...drizzle, description: "نم‌نم باران یخ‌زده" },
  61: { ...rain, description: "باران کم" },
  63: { ...rain, description: "باران متوسط" },
  65: { ...rain, description: "باران شدید" },
  66: { ...rain, description: "باران یخ‌زده" },
  67: { ...rain, description: "باران یخ‌زده" },
  71: { ...snow, description: "برف کم" },
  73: { ...snow, description: "برف متوسط" },
  75: { ...snow, description: "برف شدید" },
  77: {
    icon: SnowIcon,
    description: "دانه‌های برف",
    className: "[&>path]:stroke-[#005E98]",
  },
  80: { ...thunder, description: "رگبار باران" },
  81: { ...thunder, description: "رگبار باران" },
  82: { ...thunder, description: "رگبار باران شدید" },
  85: { ...snow, description: "رگبار برف" },
  86: { ...snow, description: "رگبار برف" },
  95: thunder,
  96: { ...thunder, description: "رعد و برق با تگرگ" },
  99: { ...thunder, description: "رعد و برق با تگرگ" },
};

export function getWeatherPresentation(code: number): WeatherPresentation {
  return weatherMap[code] ?? weatherMap[0];
}
